import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getApiBaseUrl } from './utils/api';
import './Settings.css';

const Settings = ({ onBack }) => {
  const { selectedTenant } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showCreateTenant, setShowCreateTenant] = useState(false);
  const [newTenantName, setNewTenantName] = useState('');
  const [newTenantError, setNewTenantError] = useState('');
  const [creatingTenant, setCreatingTenant] = useState(false);
  
  // Account data for dropdowns
  const [cloudFactoryAccounts, setCloudFactoryAccounts] = useState([]);
  const [dattoAccounts, setDattoAccounts] = useState([]);
  const [itGlueAccounts, setItGlueAccounts] = useState([]);
  const [zendeskAccounts, setZendeskAccounts] = useState([]);
  
  const [formData, setFormData] = useState({
    // Tenant Status
    IsActive: false,
    
    // Intune Integration
    IntuneTenant: '',
    IntuneClientId: '',
    IntuneClientSecret: '',
    
    // IT Glue Integration
    ITGlueOrgId: '',
    
    // Datto Integration
    DattoSiteUid: '',
    
    // Zendesk Integration
    ZenDeskorganization_id: '',
    
    // CloudFactory Integration
    CFCustomerId: ''
  });

  // Fetch tenant settings and account data on component mount
  useEffect(() => {
    fetchAccountData();
    if (selectedTenant) {
      fetchTenantSettings();
    }
  }, [selectedTenant]);

  const fetchAccountData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      
      // Fetch all account data in parallel
      const [cfResponse, dattoResponse, itGlueResponse, zendeskResponse] = await Promise.all([
        fetch(`${getApiBaseUrl()}/api/accounts/cloudfactory`, { headers }),
        fetch(`${getApiBaseUrl()}/api/accounts/datto`, { headers }),
        fetch(`${getApiBaseUrl()}/api/accounts/itglue`, { headers }),
        fetch(`${getApiBaseUrl()}/api/accounts/zendesk`, { headers })
      ]);
      
      if (cfResponse.ok) {
        const data = await cfResponse.json();
        setCloudFactoryAccounts(data.accounts || []);
      }
      
      if (dattoResponse.ok) {
        const data = await dattoResponse.json();
        setDattoAccounts(data.accounts || []);
      }
      
      if (itGlueResponse.ok) {
        const data = await itGlueResponse.json();
        setItGlueAccounts(data.accounts || []);
      }
      
      if (zendeskResponse.ok) {
        const data = await zendeskResponse.json();
        setZendeskAccounts(data.accounts || []);
      }
    } catch (err) {
      console.error('Error fetching account data:', err);
    }
  }, []);

  const fetchTenantSettings = useCallback(async () => {
    if (!selectedTenant) {
      setError('No tenant selected');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${getApiBaseUrl()}/api/tenant-settings?tenant=${encodeURIComponent(selectedTenant.name)}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch tenant settings');
      }

      const data = await response.json();
      
      // Convert database values to form data
      setFormData({
        IsActive: data.IsActive === 1 || data.IsActive === true,
        IntuneTenant: data.IntuneTenant || '',
        IntuneClientId: data.IntuneClientId || '',
        IntuneClientSecret: data.IntuneClientSecret || '',
        ITGlueOrgId: data.ITGlueOrgId || '',
        DattoSiteUid: data.DattoSiteUid || '',
        ZenDeskorganization_id: data.ZenDeskorganization_id || '',
        CFCustomerId: data.CFCustomerId || ''
      });
    } catch (err) {
      console.error('Error fetching tenant settings:', err);
      setError('Failed to load tenant settings');
    } finally {
      setLoading(false);
    }
  }, [selectedTenant]);

  // Fetch tenant settings and account data on component mount
  useEffect(() => {
    fetchAccountData();
    if (selectedTenant) {
      fetchTenantSettings();
    }
  }, [selectedTenant, fetchAccountData, fetchTenantSettings]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Special handling for Intune tenant domain: remove all whitespace
    const sanitizedValue = name === 'IntuneTenant'
      ? value.replace(/\s+/g, '')
      : value;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : sanitizedValue
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedTenant) {
      setError('No tenant selected');
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);
      
      const response = await fetch(`${getApiBaseUrl()}/api/tenant-settings?tenant=${encodeURIComponent(selectedTenant.name)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to update tenant settings');
      }

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error updating tenant settings:', err);
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    fetchTenantSettings();
  };

  const handleToggleActive = async () => {
    const newActiveState = !formData.IsActive;
    
    // Update local state immediately for better UX
    setFormData(prev => ({
      ...prev,
      IsActive: newActiveState
    }));
    
    try {
      setSaving(true);
      setError(null);
      
      const response = await fetch(`${getApiBaseUrl()}/api/tenant-settings?tenant=${encodeURIComponent(selectedTenant.name)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          IsActive: newActiveState
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update tenant status');
      }

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error updating tenant status:', err);
      setError('Failed to update tenant status');
      // Revert state on error
      setFormData(prev => ({
        ...prev,
        IsActive: !newActiveState
      }));
    } finally {
      setSaving(false);
    }
  };

  const handleCreateTenant = async () => {
    const nameTrimmed = newTenantName.trim();
    if (!nameTrimmed) {
      setError('Tenant name cannot be empty');
      return;
    }
    // Allow only letters, numbers, and spaces
    if (!/^[A-Za-z0-9 ]+$/.test(nameTrimmed)) {
      const message = 'Tenant name may only contain letters, numbers, and spaces';
      setNewTenantError(message);
      setError(message);
      return;
    }
    
    try {
      setCreatingTenant(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/api/tenants/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ tenantName: nameTrimmed })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create tenant');
      }

      setSuccess(true);
      setNewTenantName('');
      setNewTenantError('');
      setShowCreateTenant(false);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error creating tenant:', err);
      setError(err.message || 'Failed to create tenant');
    } finally {
      setCreatingTenant(false);
    }
  };

  if (!selectedTenant) {
    return (
      <div className="settings-container">
        <div className="settings-header">
          <button onClick={onBack} className="back-button">
            ← Back to Dashboard
          </button>
          <h1>Settings</h1>
        </div>
        <div className="error-message">Please select a tenant to view settings.</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="settings-container">
        <div className="settings-header">
          <button onClick={onBack} className="back-button">
            ← Back to Dashboard
          </button>
          <h1>Settings</h1>
        </div>
        <div className="loading-message">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <div className="settings-header">
        <div className="settings-header-buttons">
          <button 
            onClick={() => setShowCreateTenant(!showCreateTenant)} 
            className="create-tenant-button"
            disabled={creatingTenant}
          >
            {showCreateTenant ? '✕ Cancel' : '+ Create Tenant'}
          </button>
          <button onClick={onBack} className="back-button">
            ← Back to Dashboard
          </button>
        </div>
        <div>
          <h1>Integration Settings</h1>
          <p style={{color: 'rgba(255, 255, 255, 0.8)', margin: '0.5rem 0 0 0', fontSize: '1.1rem', fontWeight: '400'}}>
            Configure your tenant integration settings
          </p>
        </div>
      </div>

      {showCreateTenant && (
        <div className="create-tenant-form">
          <h3>Create New Tenant</h3>
          <div className="create-tenant-input-group">
            <input
              type="text"
              value={newTenantName}
              onChange={(e) => {
                const next = e.target.value;
                setNewTenantName(next);
                const trimmed = next.trim();
                if (!trimmed) {
                  setNewTenantError('');
                } else if (!/^[A-Za-z0-9 ]+$/.test(trimmed)) {
                  setNewTenantError('Tenant name may only contain letters, numbers, and spaces');
                } else {
                  setNewTenantError('');
                }
              }}
              placeholder="Enter tenant name"
              className="create-tenant-input"
              disabled={creatingTenant}
            />
            {newTenantError && (
              <div className="input-error" style={{ color: '#ff6b6b', fontSize: '0.9rem', marginTop: '0.25rem' }}>
                {newTenantError}
              </div>
            )}
            <button 
              onClick={handleCreateTenant} 
              className="create-tenant-submit"
              disabled={creatingTenant || !newTenantName.trim() || !!newTenantError}
            >
              {creatingTenant ? 'Creating...' : 'Create'}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {success && (
        <div className="success-message">
          Settings saved successfully!
        </div>
      )}

      <form className="settings-form" onSubmit={handleSubmit}>
        {/* Tenant Status */}
        <div className="form-section-compact">
          <div className="section-header-compact">
            <h2>Tenant Status</h2>
          </div>
          <div className="form-group">
            <button
              type="button"
              onClick={handleToggleActive}
              className={`tenant-status-toggle ${formData.IsActive ? 'active' : 'inactive'}`}
              disabled={saving}
            >
              <span className="status-indicator"></span>
              <span className="status-text">
                {formData.IsActive ? 'Active' : 'Disabled'}
              </span>
            </button>
          </div>
        </div>

        {/* Microsoft Intune Integration */}
        <div className="form-section-compact">
          <div className="section-header-compact">
            <h2>Microsoft Intune</h2>
            <span className="section-badge">Authentication</span>
          </div>
          
          <div className="form-grid-3">
            <div className="form-group">
              <label htmlFor="IntuneTenant">Tenant Domain</label>
              <input
                type="text"
                id="IntuneTenant"
                name="IntuneTenant"
                value={formData.IntuneTenant}
                onChange={handleInputChange}
                placeholder="contoso.onmicrosoft.com"
              />
            </div>
            <div className="form-group">
              <label htmlFor="IntuneClientId">Client ID</label>
              <input
                type="text"
                id="IntuneClientId"
                name="IntuneClientId"
                value={formData.IntuneClientId}
                onChange={handleInputChange}
                placeholder="00000000-0000-0000-0000-000000000000"
              />
            </div>
            <div className="form-group">
              <label htmlFor="IntuneClientSecret">Client Secret</label>
              <input
                type="password"
                id="IntuneClientSecret"
                name="IntuneClientSecret"
                value={formData.IntuneClientSecret}
                onChange={handleInputChange}
                placeholder="••••••••••••••••"
              />
            </div>
          </div>
        </div>

        {/* All Other Integrations */}
        <div className="form-section-compact">
          <div className="section-header-compact">
            <h2>Service Integrations</h2>
            <span className="section-badge">Organizations</span>
          </div>
          
          <div className="form-grid-2">
            <div className="form-group">
              <label htmlFor="ITGlueOrgId">
                <span className="integration-icon">📚</span> IT Glue Organization
              </label>
              <select
                id="ITGlueOrgId"
                name="ITGlueOrgId"
                value={formData.ITGlueOrgId}
                onChange={handleInputChange}
                className="form-select"
              >
                <option value="">-- Select Organization --</option>
                {itGlueAccounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="DattoSiteUid">
                <span className="integration-icon">🔒</span> Datto Site
              </label>
              <select
                id="DattoSiteUid"
                name="DattoSiteUid"
                value={formData.DattoSiteUid}
                onChange={handleInputChange}
                className="form-select"
              >
                <option value="">-- Select Site --</option>
                {dattoAccounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="ZenDeskorganization_id">
                <span className="integration-icon">🎫</span> Zendesk Organization
              </label>
              <select
                id="ZenDeskorganization_id"
                name="ZenDeskorganization_id"
                value={formData.ZenDeskorganization_id}
                onChange={handleInputChange}
                className="form-select"
              >
                <option value="">-- Select Organization --</option>
                {zendeskAccounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="CFCustomerId">
                <span className="integration-icon">☁️</span> CloudFactory Customer
              </label>
              <select
                id="CFCustomerId"
                name="CFCustomerId"
                value={formData.CFCustomerId}
                onChange={handleInputChange}
                className="form-select"
              >
                <option value="">-- Select Customer --</option>
                {cloudFactoryAccounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" onClick={handleReset} className="reset-button" disabled={saving}>
            Reset
          </button>
          <button type="submit" className="save-button" disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Settings;